/* eslint-disable no-undef, react/no-multi-comp, filenames/match-exported */
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet-async'
import Fuse from 'fuse.js'
import { debounce } from '../../helpers/debounce'
import { centreType } from '../../types'
import { CONFIG_KEYS, getClientConfigValue } from '../../config'
import SearchField from '../search-field'
import { addToQuery } from '../../helpers/url'
import style from './index.module.scss'
import Store from './store'
import isMigrated from './is-migrated'

require('./map-bundler')

const scripts = [
  '/static/js/moment.static.js',
  '/static/js/moment-timezone-with-data-2012-2022.static.js',
  '/static/js/hammer.static.js',
  '/static/js/react-with-addons.static.js',
  '/static/js/react-dom.static.js',
  '/static/js/rome.static.js',
  // Must add js files as `.static.js` so webpack knows what to do with them
]

const CentreMap = props => {
  if (isMigrated(props.centre.slug)) {
    return <CentreMap4 {...props} />
  } else {
    return <CentreMap3 {...props} />
  }
}

export class CentreMap4 extends Component {
  state = {
    filteredStores: [],
    selectedStore: null,
    searchValue: '',
    ready: false,
  }

  componentDidMount() {
    window.addEventListener('message', this.receivePostMessage)
    window.addEventListener('storage', this.clearSelectedStore)
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.receivePostMessage)
  }

  clearSelectedStore = () => {
    // eslint-disable-next-line no-unused-expressions
    this.state.selectedStore !== null && this.setState({ selectedStore: null })
  }
  //
  // Find a store by the legacyIdentifier.
  //
  // NOTE: may return undefined.
  //
  findStore = externalId =>
    this.props.stores.find(
      store =>
        // eslint-disable-next-line eqeqeq
        store.legacyIdentifier == externalId || store.sys.id === externalId
    )

  //
  // Highlight selected store by showing at bottom of page.
  // If no store was found, we clear the selectedStore state.
  //
  showStore = externalId => {
    const store = this.findStore(externalId)
    if (store) {
      this.setState({ selectedStore: store })
    } else {
      this.deselectStore()
    }
  }

  deselectStore = () => {
    this.setState({ selectedStore: null })
  }

  receivePostMessage = message => {
    if (message.origin !== window.location.origin) {
      return
    }

    if (message.data.event === 'ready') {
      this.setState({ ready: true })
    }

    if (message.data.event === 'jibestream-more-info') {
      const { externalId } = message.data
      this.showStore(externalId)
    }
  }

  updateSearchResults = () => {
    const searchValue = this.searchField.value
    this.setState({
      searchValue,
      filteredStores: this.searchStores(searchValue),
    })
  }

  handleOnChangeSearch = debounce(this.updateSearchResults)

  searchStores = term => {
    if (term.length < 1) {
      return []
    }

    const options = {
      shouldSort: true,
      threshold: 0.3,
      location: 0,
      keys: ['title'],
    }

    return new Fuse(this.props.stores, options).search(term)
  }

  selectStore = (event, selectedStore) => {
    event.preventDefault()
    this.setState({ selectedStore, filteredStores: [] })
    this.mapFrame.contentWindow.postMessage(
      {
        event: 'select-store',
        legacyId: selectedStore.legacyIdentifier,
        entryid: selectedStore.sys.id,
      },
      window.location.origin
    )
  }

  render() {
    const {
      centre: { title: centreName, slug },
      legacyIdentifier,
      entryId,
    } = this.props

    const jibestreamV4VenueId = isMigrated(slug)
    return (
      <Fragment>
        <Helmet>
          <link
            rel="stylesheet"
            type="text/css"
            href="/static/css/maps.static.css"
          />
        </Helmet>
        <iframe
          title="Centre Map"
          id="map"
          className="mapFrame"
          ref={elem => (this.mapFrame = elem)}
          src={`/jibestream-map/map.html?venueId=${jibestreamV4VenueId}&externalId=${legacyIdentifier}&entryId=${entryId}`}
        />
        {this.state.ready && (
          <div className={style.searchField}>
            <SearchField
              id="searchInp"
              value={this.state.searchValue}
              placeholder={`Find stores in ${centreName}`}
              innerRef={elem => (this.searchField = elem)}
              autoCorrect="off"
              autoComplete="off"
              onChange={this.handleOnChangeSearch}
            />
            {this.state.filteredStores.length > 0 && (
              <div className={style.filteredStores}>
                {this.state.filteredStores.map((filteredStore, index) => (
                  <Store
                    dataTestId="filtered-store-name"
                    store={filteredStore}
                    key={`map-store-${index}`}
                    selectStore={event =>
                      this.selectStore(event, filteredStore)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {this.state.selectedStore && (
          <div className={style.selectedStore}>
            <Store
              dataTestId="selected-store-name"
              store={this.state.selectedStore}
              deselectStore={this.deselectStore}
            />
          </div>
        )}
      </Fragment>
    )
  }
}

class CentreMap3 extends Component {
  componentDidMount() {
    scripts.forEach(script => {
      const mapScript = document.createElement('script')
      mapScript.src = script
      mapScript.async = false
      if (script === '/static/js/rome.static.js') {
        const centreName = this.props.centre.slug
        mapScript.onload = () => {
          const map = new rome.Map('#map', {
            apiKey: getClientConfigValue(
              CONFIG_KEYS.NEXT_PUBLIC_SCENTREGROUP_API_KEY
            ),
            centreId: centreName,
            endpoint: {
              apiHostname: getClientConfigValue(
                CONFIG_KEYS.NEXT_PUBLIC_SCENTREGROUP_API
              ),
              mapHostname: getClientConfigValue(
                CONFIG_KEYS.NEXT_PUBLIC_MAPS_API
              ),
            },
            showContextualSearch: true,
            onBeforeReady: () => {
              const legacyIdentifier = parseInt(this.props.legacyIdentifier, 10)
              if (legacyIdentifier) {
                return map.selectUnitByStoreId(legacyIdentifier, {
                  animate: true,
                })
              }
              return null
            },
            middleware: {
              url: url => {
                if (url.match(/maps.*\.io\/cms\//)) {
                  return addToQuery(url, 'centreId', centreName)
                }
                return url
              },
            },
          })
          this.map = map
        }
      }
      document.body.appendChild(mapScript)
    })
  }

  componentWillUnmount() {
    this.map.destroy()
    scripts.forEach(script => {
      const mapScript = document.querySelector(`script[src='${script}']`)
      document.body.removeChild(mapScript)
    })
    window.React = null
    window.ReactDOM = null
  }

  render() {
    return (
      <Fragment>
        <Helmet>
          <link
            key="rome-static-css"
            rel="stylesheet"
            type="text/css"
            href="/static/css/rome.static.css"
          />
          <link
            key="maps-static-css"
            rel="stylesheet"
            type="text/css"
            href="/static/css/maps.static.css"
          />
        </Helmet>
        <div id="map" />
      </Fragment>
    )
  }
}

CentreMap.propTypes = {
  centre: centreType,
}

CentreMap3.propTypes = {
  centre: centreType,
  legacyIdentifier: PropTypes.string,
}

CentreMap4.propTypes = {
  centre: centreType,
  stores: PropTypes.array,
  legacyIdentifier: PropTypes.string,
  entryId: PropTypes.string,
}

export default CentreMap
